/** Entity XML/JSON document exports (console).
 *
 *  `GET /entity_exports/contract/` describes, per exportable entity
 *  (activity, itinerary, tour, booking, user), the available formats and the
 *  optional content "hints" an export can exclude.
 *
 *  `POST /<resource>/document_export/` starts a background export over a grid
 *  selection — 202 = queued on the background-task framework (the finished
 *  file appears as a Background Task artifact), 204 = the selection resolved
 *  to nothing. As with `/batch/`, the caller MUST forward the grid's current
 *  list query string so `all_matching` resolves the same set the grid shows.
 *
 *  Hand-authored (console convention: feature modules never depend on the
 *  generated types); uses the shared axios client from `./client`.
 */
import type { BatchSelection } from './batch';
import { apiGet, client } from './client';

/** One optional content block the export can include (default) or exclude. */
export interface EntityExportHint {
  key: string;
  label: string;
  description: string;
  default: boolean;
}

export interface EntityExportEntityContract {
  /** Supported serialization formats, e.g. `["xml", "json"]`. */
  formats: string[];
  hints: EntityExportHint[];
}

/** Keyed by entity: activity, itinerary, tour, booking, user. */
export type EntityExportContract = Record<string, EntityExportEntityContract>;

export function fetchEntityExportContract(): Promise<EntityExportContract> {
  return apiGet<EntityExportContract>('/entity_exports/contract/');
}

export type EntityExportFormat = 'xml' | 'json';

export interface DocumentExportBody {
  format: EntityExportFormat;
  selection: BatchSelection;
  /** Hint keys to leave OUT of the document (everything else is included). */
  hints?: { exclude: string[] };
  /** Optional Import_Source code — stamps a `<sources>` block so the file can
   *  be re-imported under that source. Mutually exclusive with tenant_files. */
  source_id?: string;
  /** Multi-tenant export: each record gets its OWNING operator's default
   *  import key; operators without one are skipped (error in the task log).
   *  'combined' = one document, 'per_tenant' = one artifact per operator. */
  tenant_files?: 'combined' | 'per_tenant';
}

/** 202 payload: the export was queued on the background-task framework. */
export interface DocumentExportResponse {
  task_id: string;
  task_type: string;
  task_label: string;
  state: number;
  mode: 'async';
  matched: number | null;
  processed: number | null;
  capped: boolean;
}

/** POST `/<resource>/document_export/<?filterQuery>`.
 *
 *  `filterQuery` is the grid's current list query string (search + filters +
 *  tenant + ordering) WITHOUT a leading `?` — same contract as `runBatch`.
 *  Resolves to the 202 payload, or `null` on a 204 (nothing to export). */
export async function runDocumentExport(
  resourcePath: string,
  body: DocumentExportBody,
  filterQuery?: string,
): Promise<DocumentExportResponse | null> {
  const qs = filterQuery ? `?${filterQuery}` : '';
  const r = await client.post<DocumentExportResponse>(
    `/${resourcePath}/document_export/${qs}`,
    body,
  );
  return r.status === 204 ? null : r.data;
}
